// Admin: create, edit and delete events (all types for super admins, blåtur for blåtur organizers)
// Saving recalculates everyone's points, since points are stored per user

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs, doc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { FiPlus, FiX, FiTrash2 } from 'react-icons/fi';
import { app } from './firebase-config';
import { EVENT_TYPES, normalizeName, createPerson } from './adminData';
import { BLATUR_TYPE } from './blaturData';
import { recalculateUserPoints } from './recalculatePoints';
import AdminImport from './AdminImport';
import { quarterOf } from './upcoming';
import { SmartImage } from './imageUtils';

const db = getFirestore(app);
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const emptyEvent = (type) => ({
  sted_konsept: '',
  event_type: type,
  year: new Date().getFullYear(),
  quarter: `Q${Math.floor(new Date().getMonth() / 3) + 1}`,
  Vert: [],
  Deltagelse: [],
  description: '',
  coverImage: '',
  date: '',
  location: '',
  tags: '',
});

const toForm = (event) => {
  const [year, quarter] = (event.Kvartal || '').split(' ');
  return {
    ...emptyEvent(event.event_type),
    ...event,
    year: parseInt(year, 10) || new Date().getFullYear(),
    quarter: quarter || 'Q1',
    description: event.description || '',
    coverImage: event.coverImage || '',
    date: event.date || '',
    location: event.location || '',
    tags: (event.tags || []).join(', '),
  };
};

// Pick names from everyone the app knows, or add someone new to the people register
export function NamePicker({ label, value, onChange, names, onCreatePerson }) {
  const [input, setInput] = useState('');
  const known = useMemo(() => new Map(names.map(n => [normalizeName(n), n])), [names]);
  const match = known.get(normalizeName(input));
  const listId = `names-${label}`;

  const add = (name) => {
    if (name && !value.includes(name)) onChange([...value, name]);
    setInput('');
  };

  const createAndAdd = async () => {
    const name = input.trim();
    if (!name) return;
    if (!window.confirm(`«${name}» finnes ikke fra før. Legge til som ny person?`)) return;
    await onCreatePerson(name);
    add(name);
  };

  return (
    <div className="admin-field">
      <label>{label} ({value.length})</label>
      <div className="admin-chips">
        {value.map(name => (
          <span key={name} className={`admin-chip${known.has(normalizeName(name)) ? '' : ' unknown'}`} title={known.has(normalizeName(name)) ? '' : 'Ukjent navn'}>
            {name}
            <button type="button" onClick={() => onChange(value.filter(n => n !== name))} aria-label={`Fjern ${name}`}><FiX /></button>
          </span>
        ))}
      </div>
      <div className="admin-picker">
        <input
          list={listId}
          value={input}
          placeholder="Skriv navn…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (match) add(match);
            }
          }}
        />
        <datalist id={listId}>
          {names.filter(n => !value.includes(n)).map(n => <option key={n} value={n} />)}
        </datalist>
        {match ? (
          <button type="button" className="admin-btn" onClick={() => add(match)}><FiPlus /> Legg til</button>
        ) : input.trim() ? (
          <button type="button" className="admin-btn" onClick={createAndAdd}><FiPlus /> Ny person</button>
        ) : null}
      </div>
    </div>
  );
}

// Separate and memoized, so typing in the form doesn't re-render dozens of photos
const CoverPicker = memo(function CoverPicker({ images, selected, onSelect }) {
  return (
    <div className="admin-field">
      <label>Gruppebilde</label>
      <div className="admin-covers">
        {images.map(url => (
          <button
            type="button"
            key={url}
            className={`admin-cover${selected === url ? ' selected' : ''}`}
            onClick={() => onSelect(url)}
          >
            <SmartImage src={url} size={200} />
          </button>
        ))}
      </div>
    </div>
  );
});

function EventForm({ initial, isNew, allowedTypes, names, canDelete, onCreatePerson, onSaved, onCancel }) {
  const [form, setForm] = useState(() => toForm(initial));
  const [saving, setSaving] = useState('');
  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const selectCover = useCallback((url) => setForm(f => ({ ...f, coverImage: url })), []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.sted_konsept.trim()) return alert('Fyll inn sted/konsept.');
    // Hosts also count as participants
    const participants = [...form.Deltagelse, ...form.Vert.filter(n => !form.Deltagelse.includes(n))];
    const data = {
      sted_konsept: form.sted_konsept.trim(),
      event_type: form.event_type,
      Kvartal: form.date ? quarterOf(form.date) : `${form.year} ${form.quarter}`,
      date: form.date || null,
      location: form.location.trim(),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      Vert: form.Vert,
      Deltagelse: participants,
      description: form.description,
      ...(form.coverImage ? { coverImage: form.coverImage } : {}),
    };
    try {
      setSaving('Lagrer…');
      if (isNew) await addDoc(collection(db, 'events'), { ...data, images: [] });
      else await updateDoc(doc(db, 'events', initial.id), data);
      setSaving('Oppdaterer poeng…');
      await recalculateUserPoints();
      onSaved();
    } catch (error) {
      console.error('Error saving event: ', error);
      alert('Kunne ikke lagre: ' + error.message);
      setSaving('');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Slette «${initial.sted_konsept}» (${initial.Kvartal}) for godt? Bilder ligger igjen i lagringen.`)) return;
    try {
      setSaving('Sletter…');
      await deleteDoc(doc(db, 'events', initial.id));
      await recalculateUserPoints();
      onSaved();
    } catch (error) {
      alert('Kunne ikke slette: ' + error.message);
      setSaving('');
    }
  };

  return (
    <form className="admin-form" onSubmit={save}>
      <h2>{isNew ? 'Nytt event' : `Rediger ${initial.sted_konsept}`}</h2>

      <div className="admin-row">
        <div className="admin-field grow">
          <label>Sted/konsept</label>
          <input
            value={form.sted_konsept}
            onChange={(e) => set('sted_konsept', e.target.value)}
            placeholder={form.event_type === BLATUR_TYPE ? 'Land-Sted, f.eks. Italia-Toscana' : 'F.eks. Engebret Cafe'}
          />
        </div>
        <div className="admin-field">
          <label>Type</label>
          <select value={form.event_type} onChange={(e) => set('event_type', e.target.value)}>
            {allowedTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="admin-field small">
          <label>År</label>
          <input type="number" value={form.year} onChange={(e) => set('year', e.target.value)} disabled={!!form.date} />
        </div>
        <div className="admin-field small">
          <label>Kvartal</label>
          <select value={form.quarter} onChange={(e) => set('quarter', e.target.value)} disabled={!!form.date}>
            {QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
      </div>

      <div className="admin-row">
        <div className="admin-field">
          <label>Dato</label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => {
              const date = e.target.value;
              setForm(f => (date
                ? { ...f, date, year: new Date(date).getFullYear(), quarter: quarterOf(date).split(' ')[1] }
                : { ...f, date: '' }));
            }}
          />
        </div>
        <div className="admin-field grow">
          <label>Sted / adresse</label>
          <input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="F.eks. Rådhusgata 17, kl 17:00" />
        </div>
        <div className="admin-field grow">
          <label>Stikkord (kommaseparert)</label>
          <input value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="F.eks. KL rookie, Asiatisk" />
        </div>
      </div>
      <p className="admin-hint">
        Kommende eventer vises automatisk på forsiden: med dato til dagen har vært, og uten dato bare hvis kvartalet ikke har begynt ennå.
      </p>

      <NamePicker label="Arrangører" value={form.Vert} onChange={(v) => set('Vert', v)} names={names} onCreatePerson={onCreatePerson} />
      <NamePicker label="Deltakere" value={form.Deltagelse} onChange={(v) => set('Deltagelse', v)} names={names} onCreatePerson={onCreatePerson} />
      <p className="admin-hint">Arrangører regnes også som deltakere, og legges til automatisk ved lagring.</p>

      <div className="admin-field">
        <label>Beskrivelse</label>
        <textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>

      {(initial.images || []).length > 0 && (
        <CoverPicker images={initial.images} selected={form.coverImage || initial.images[0]} onSelect={selectCover} />
      )}

      <div className="admin-actions">
        {saving ? <span className="admin-status">{saving}</span> : (
          <>
            <button type="submit" className="button-17">Lagre</button>
            <button type="button" className="admin-btn" onClick={onCancel}>Avbryt</button>
            {canDelete && !isNew && (
              <button type="button" className="admin-btn danger" onClick={remove}><FiTrash2 /> Slett</button>
            )}
          </>
        )}
      </div>
    </form>
  );
}

function AdminEvents({ user, roles, directory, reloadDirectory, initialEventId }) {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // event, or { isNew: true }
  const [typeFilter, setTypeFilter] = useState(roles.isSuperAdmin ? '' : BLATUR_TYPE);

  const allowedTypes = roles.isSuperAdmin ? EVENT_TYPES : [BLATUR_TYPE];

  const load = async () => {
    const snap = await getDocs(collection(db, 'events'));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(e => roles.isSuperAdmin || e.event_type === BLATUR_TYPE);
    list.sort((a, b) => (b.Kvartal || '').localeCompare(a.Kvartal || ''));
    setEvents(list);
    return list;
  };

  useEffect(() => {
    load().then(list => {
      const initial = initialEventId && list.find(e => e.id === initialEventId);
      if (initial) setEditing(initial);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onCreatePerson = async (name) => {
    await createPerson({ name }, user.uid);
    await reloadDirectory();
  };

  if (editing) {
    return (
      <EventForm
        key={editing.id || 'new'}
        initial={editing.isNew ? emptyEvent(typeFilter || allowedTypes[0]) : editing}
        isNew={!!editing.isNew}
        allowedTypes={allowedTypes}
        names={directory.names}
        canDelete={roles.isSuperAdmin}
        onCreatePerson={onCreatePerson}
        onCancel={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await load(); }}
      />
    );
  }

  const visible = typeFilter ? events.filter(e => e.event_type === typeFilter) : events;

  return (
    <div>
      {roles.isSuperAdmin && (
        <AdminImport user={user} events={events} directory={directory} reloadDirectory={reloadDirectory} onDone={load} />
      )}
      <div className="admin-toolbar">
        {roles.isSuperAdmin && (
          <div className="admin-filter">
            <button className={`filter-chip${typeFilter === '' ? ' active' : ''}`} onClick={() => setTypeFilter('')}>Alle</button>
            {EVENT_TYPES.map(t => (
              <button key={t} className={`filter-chip${typeFilter === t ? ' active' : ''}`} onClick={() => setTypeFilter(t)}>{t}</button>
            ))}
          </div>
        )}
        <button className="button-17" onClick={() => setEditing({ isNew: true })}><FiPlus /> Nytt event</button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Sted/konsept</th><th>Type</th><th>Kvartal</th><th>Arrangører</th><th>Deltakere</th></tr>
          </thead>
          <tbody>
            {visible.map(e => (
              <tr key={e.id} onClick={() => setEditing(e)}>
                <td>{e.sted_konsept}</td>
                <td>{e.event_type}</td>
                <td>{e.Kvartal}</td>
                <td>{(e.Vert || []).join(', ')}</td>
                <td>{(e.Deltagelse || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminEvents;
